import { useCallback } from "react";
import {
  BetTicketInfoForDisplay,
  BetTicketInfoPayload,
} from "../types/betTicket";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import mutation from "../mutation";
import SportBarService from "../services/sportBar";
import query from "../query";
import produce from "immer";

const sportBarService = new SportBarService();

const useBetInfo = () => {
  const queryClient = useQueryClient();
  const updateCurrentTabIndex = useMutation(mutation.updateCurrentTabIndex, {
    onSuccess: (index) => {
      queryClient.setQueryData(["currentTabIndex"], index);
    },
  });
  const updateIsBetSlipToggle = useMutation(mutation.updateIsBetSlipToggle, {
    onSuccess: (newIsBetSlipToggle) => {
      queryClient.setQueryData(["isBetSlipToggle"], newIsBetSlipToggle);
    },
  });

  const betTypeQuery = query.betType();
  const betType = useQuery(betTypeQuery.key, betTypeQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });

  const updateBetTicketInfoPayload = useMutation(
    mutation.updateBetTicketInfoPayload,
    {
      onSuccess: (payload) => {
        queryClient.setQueryData(["betTicketInfoPayload"], payload);
      },
    },
  );

  const push = useCallback(
    async (rawPayload: BetTicketInfoPayload & BetTicketInfoForDisplay) => {
      // Callers on mobile don't always know the provider, so fall back to the
      // global provider state to keep betInfo/placeBet on the selected provider.
      const payload = {
        ...rawPayload,
        pvd:
          rawPayload.pvd ??
          queryClient.getQueryData<string>(query.provider().key),
      };
      await updateCurrentTabIndex.mutateAsync(0);

      if (betType?.data === "normal") {
        await updateBetTicketInfoPayload.mutate([payload]);
        await updateIsBetSlipToggle.mutate(true);
        sportBarService.collapse();
      } else {
        const betTicketInfoPayloads =
          queryClient.getQueryData<
            (BetTicketInfoPayload & BetTicketInfoForDisplay)[]
          >(["betTicketInfoPayload"]) ?? [];
        const index = betTicketInfoPayloads.findIndex(
          (betTicketInfoPayload) => {
            return betTicketInfoPayload.matchId === payload.matchId;
          },
        );

        if (index >= 0) {
          const newBetTicketInfoPayload = produce(
            betTicketInfoPayloads,
            (draft) => {
              draft[index] = payload;
            },
          );
          await updateBetTicketInfoPayload.mutate(newBetTicketInfoPayload);
          sportBarService.collapse();
        } else {
          await updateBetTicketInfoPayload.mutate([
            ...betTicketInfoPayloads,
            payload,
          ]);
          sportBarService.collapse();
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [betType?.data],
  );

  const set = useCallback((payload: any) => {
    updateBetTicketInfoPayload.mutate(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    push,
    set,
  };
};

export default useBetInfo;
